<script lang="typescript">
    import slugify from "slugify"
    import { activeNav, jsonForOverlay, apiToken, apiBase } from "../store"

    import JsonOverlay from "./JsonOverlay.svelte"
    import Loader from "./Loader.svelte"
    import NavTree from "./NavTree.svelte"
    import ProductTable from "./ProductTable.svelte"
    import { parseExcel } from "../excel"
    import { api } from "../api"
    import { diffNavEntry, diffProductEntry } from "../diff"
    import SyncQueueOverlay from "./SyncQueueOverlay.svelte"
    import config from "../config"

    let stats = {
        all: 0,
        new: 0,
        change: 0,
        delete: 0,
    }

    let showSyncQueue = false

    let loaderText = ""
    let files

    let navMap: {
        [key: string]: Nav
    } = {}

    let productMap: {
        [key: string]: Product
    } = {}

    let nav: Nav

    let toSync: SyncEntry[] = []

    $: if (files?.length) {
        console.log(files)
        prepareData()
    }

    const clearState = () => {
        nav = {
            id: "0".padStart(24, "0"),
            name: {
                de: "root",
                en: "root",
                fr: "root",
                se: "root",
                dk: "root",
            },
            path: { de: "", en: "", fr: "", se: "", dk: "" },
            subNav: [],
            products: [],
            productsNew: 0,
            productsChange: 0,
            productsDelete: 0,
        }
        $activeNav = null
        navMap = {}
        navMap[nav.id] = nav // root
        productMap = {}
        toSync = []

        stats.all = 0
        stats.new = 0
        stats.change = 0
        stats.delete = 0
    }

    const prepareData = async () => {
        clearState()
        try {
            loaderText = "lese Excel-Datei..."
            await loadExcel(files[0])

            loaderText = "lese online Kategorien..."
            await getOnlineCategories()

            loaderText = "lese online Produkte..."
            await getOnlineProducts()

            loaderText = "ermittle Unterschied..."
            diffData(nav)
            toSync = toSync
            loaderText = ""
        } catch (e) {
            const msg = "FEHLER: " + (e.response?.data?.error ?? "siehe Javascript Konsole")
            loaderText = msg
            console.log(e)
        } finally {
            nav = nav
        }
    }

    const addProductToNav = (nav: Nav, xlsRow: any) => {
        let code: string = xlsRow["Item number"]

        let xFarbe: string = "0000"
        if (xlsRow.xFarbe) {
            xFarbe = xlsRow.xFarbe.toString().padStart(4, "0")
            code += "-" + xFarbe
        }

        if (xlsRow["BK_Sprechfeld_Klingel"] == 1) {
            xlsRow["xSprechfeld"] = 1 // ohne
        }
        if (xlsRow["BK_Sprechfeld_Klingel_oben"] == 1) {
            xlsRow["xSprechfeld"] = 4 // oben
        }

        let xSprech: string = "00"
        if (xlsRow.xSprechfeld) {
            xSprech = xlsRow.xSprechfeld.toString().padStart(2, "0")
            code += "-" + xSprech
        }

        const sprechReplaceMap = {
            2: { de: "Sprech/Klingel rechts" },
            3: { de: "Sprech/Klingel links" },
            4: { de: "Sprech/Klingel oben" },
        }

        const replaceSprechStr = sprechReplaceMap[xlsRow["xSprechfeld"]]
        if (replaceSprechStr) {
            xlsRow.Title = xlsRow.Title.toString().replace("mit Sprech/Klingel", replaceSprechStr.de)
        }

        let newProduct: Product = {
            id: (
                xlsRow["Item number"]
                    .toString()
                    .toLowerCase()
                    .replace(/[^a-f0-9]/gi, "0") +
                xFarbe +
                xSprech +
                "000000"
            ).padStart(24, "0"),
            code,
            name: {},
            nav,
            xlsRow,
        }
        newProduct.name[config.defaultLanguage] = xlsRow.Title

        nav.products.push(newProduct)
        productMap[newProduct.id] = newProduct
    }

    const loadExcel = async (file) => {
        let currentNav = { 0: nav }

        let rows = await parseExcel(file)
        stats.all = rows.length

        let l = 0
        for (let i = 0; i < rows.length; i++) {
            if (rows[i].NavID) {
                // is nav
                let name: string
                for (l = 1; l <= 5; l++) {
                    name = rows[i]["Nav" + l]
                    if (name) break
                }
                if (name) {
                    // is navi with name, so use it
                    let newNav: Nav = {
                        id: rows[i].NavID.toString().padStart(24, "0"),
                        name: {},
                        path: {},
                        xlsRow: rows[i],
                        subNav: [],
                        products: [],
                        productsNew: 0,
                        productsChange: 0,
                        productsDelete: 0,
                    }
                    newNav.name[config.defaultLanguage] = name
                    newNav.path[config.defaultLanguage] =
                        currentNav[l - 1].path[config.defaultLanguage] +
                        "/" +
                        slugify(name, {
                            lower: true,
                            locale: config.defaultLanguage,
                        })

                    currentNav[l] = newNav
                    currentNav[l - 1].subNav.push(newNav)
                    navMap[newNav.id] = newNav
                }
            } else if (rows[i]["Item number"]) {
                // is product
                const splitRows: any[] = [rows[i]]

                const toSplit = [
                    {
                        target: "xFarbe",
                        splitFunc(row): any[] {
                            let farben: string = row["Available colors"]
                            if (farben && farben.toString().trim()) {
                                return farben
                                    .toString()
                                    .split(/ *[,;\n] */)
                                    .map((f) => {
                                        /*
                                        const codeMap = {
                                            gr: "7039",
                                            we: "9016",
                                            br: "0019",
                                            gl: "0000",
                                            si: "0001",
                                            bz: "0014",
                                            "sw/mi": "0971",
                                            "sw/we": "0970",
                                            "we/sw": "0973",
                                            "we/gr": "0972",
                                        }
                                        return codeMap[f.trim()]
                                        */
                                        return f.trim().padStart(4, "0")
                                    })
                            }
                        },
                        modFunc(row, isClone = false, idx = 0, count = 1) {
                            if (count > 1) {
                                /*
                                if (isClone) {
                                    if (row.Image) {
                                        row.Image = row.Image.toString().replace(
                                            /\.jpg/g,
                                            "-" + row.xFarbe + ".jpg"
                                        )
                                    }
                                }
                                */

                                const titleMap = {
                                    "7039": { de: "quarzgrau" },
                                    "9016": { de: "verkehrsweiß" },
                                    "0029": { de: "Renz-braun" },
                                    "0000": { de: "glasklar" },
                                    "0001": { de: "silber" },
                                    "0014": { de: "bronze" },
                                    "0971": {
                                        de: "schwarze Schrift / milchiger Hintergrund",
                                    },
                                    "0970": {
                                        de: "weiße Schrift / schwarzer Hintergrund",
                                    },
                                    "0973": {
                                        de: "schwarze Schrift / weißer Hintergrund",
                                    },
                                    "0972": {
                                        de: "weiße Schrift / grauer Hintergrund",
                                    },
                                }

                                row.Title =
                                    row.Title.toString() + ", " + (titleMap[row.xFarbe] && titleMap[row.xFarbe].de)
                            }
                        },
                    },
                    {
                        target: "xSprechfeld",
                        splitFunc(row): any[] {
                            if (!isNaN(parseInt(row["BK_Bruttopreis_Installationskasten_links"]))) {
                                // is 0, so is option => split
                                return [2, 3]
                            }
                        },
                        modFunc(row, isClone = false, idx = 0, count = 1) {
                            // global see above
                        },
                    },
                ]

                toSplit.forEach((s) => {
                    const l = splitRows.length

                    for (let ii = 0; ii < l; ii++) {
                        const vals = s.splitFunc(splitRows[ii])
                        // if (splitRows[ii]["Title"] == "MEFA Colin (121) Verkehrsweiß") {
                        //     console.log("split", ii, splitRows[ii], vals)
                        // }

                        if (vals) {
                            const vl = vals.length
                            for (let vi = 0; vi < vl; vi++) {
                                var orgObj
                                if (vi === 0) {
                                    // clone
                                    orgObj = { ...splitRows[ii] }
                                    // modify direct
                                    splitRows[ii][s.target] = vals[vi]
                                    s.modFunc(splitRows[ii], false, vi, vl)
                                } else {
                                    // modify clone
                                    const newO = { ...orgObj }
                                    newO[s.target] = vals[vi]
                                    s.modFunc(newO, true, vi, vl)
                                    splitRows.push(newO)
                                }
                            }
                        }
                    }
                })

                splitRows.forEach((r) => {
                    addProductToNav(currentNav[l], r)
                })
            }
        }
    }

    const getOnlineCategories = async () => {
        let categories = await api().get("category")
        categories.data?.forEach((c) => {
            //console.log(c)
            const n = navMap[c.id]
            if (n) {
                // also in xls
                n.online = c
            } else {
                navMap[c.id] = {
                    id: c.id,
                    name: c.name,
                    path: c.path,
                    online: c,
                    productsNew: 0,
                    productsChange: 0,
                    productsDelete: 0,
                }
            }
        })
        nav = nav
    }

    const getOnlineProducts = async () => {
        let products = await api().get("product")
        products.data?.forEach((p) => {
            const n = productMap[p.id]
            if (n) {
                // also in xls
                n.online = p
            } else {
                productMap[p.id] = {
                    id: p.id,
                    name: p.name,
                    code: p.code,
                    online: p,
                }
            }
        })
        nav = nav
    }

    const diffData = (n: Nav) => {
        _diffData(n)
        // add rest for deletion
        Object.keys(navMap).forEach((k) => {
            stats.delete++
            navMap[k].method = "delete"
            toSync.push({
                collection: "category",
                obj: navMap[k],
            })
            // add to end of list in view
            nav.subNav.push(navMap[k])
        })
        Object.keys(productMap).forEach((k) => {
            let p = productMap[k]

            stats.delete++
            p.method = "delete"
            toSync.push({
                collection: "product",
                obj: p,
            })

            let c = p.online?.categories
            if (c && c.length) {
                if (navMap[c[0]]) {
                    // first category found
                    p.nav = navMap[c[0]]
                    navMap[c[0]].products?.unshift(p)
                    p.nav.productsDelete++
                }
            }
        })
        navMap = {}
        productMap = {}

        console.log(toSync)
    }

    const _diffData = (n: Nav) => {
        delete navMap[n.id]
        const isDiff = diffNavEntry(n)
        if (!n.online) {
            n.method = "post"
            n.diff.id = n.id
            stats.new++
        } else if (isDiff) {
            n.method = "put"
            stats.change++
        }

        if (n.method) {
            toSync.push({
                collection: "category",
                obj: n,
            })
        }

        // products
        n.products.forEach((p) => {
            delete productMap[p.id]
            const isDiff = diffProductEntry(p)

            if (!p.online) {
                p.method = "post"
                p.diff.id = p.id
                stats.new++
                p.nav && p.nav.productsNew++
            } else if (isDiff) {
                p.method = "put"
                stats.change++
                p.nav && p.nav.productsChange++
            }

            if (p.method) {
                toSync.push({
                    collection: "product",
                    obj: p,
                })
            }
        })

        nav = nav
        n.subNav.forEach((sub) => {
            _diffData(sub)
        })
    }

    const sync = async () => {
        try {
            loaderText = "syncing..."
            await _sync()
            loaderText = ""
        } catch (e) {
            const msg = "FEHLER: " + (e.response?.data?.error ?? "see Javascript Console")
            loaderText = msg
            console.log(e)
        } finally {
            nav = nav
        }
    }

    const _sync = async () => {
        const _api = api()

        while (toSync.length > 0) {
            const entry = toSync.shift()
            const obj = entry.obj

            loaderText = `${toSync.length + 1} items to synchronize: ${entry.obj.method} ${entry.collection} - ${
                obj.name && obj.name[config.defaultLanguage]
            }`
            await _api.request({
                url: entry.collection + (obj.method != "post" ? "/" + obj.id : ""),
                method: obj.method,
                data: obj.method == "delete" ? undefined : obj.diff,
            })

            switch (obj.method) {
                case "post":
                    stats.new--
                    if (obj.nav) obj.nav.productsNew--
                    break
                case "put":
                    stats.change--
                    if (obj.nav) obj.nav.productsChange--
                    break
                case "delete":
                    stats.delete--
                    if (obj.nav) obj.nav.productsDelete--
                    break
            }

            // live state update
            delete obj.method
            nav = nav
            toSync = toSync
            $activeNav = $activeNav
        }

        loaderText = ""
    }
</script>

<div class="header" style="display:flex">
    <div style="width: 200px;">
        <input style="width: 150px;" type="password" bind:value="{$apiToken}" placeholder="API Token" />
    </div>
    <div style="padding-right: 20px;">
        <label
            >Server:
            <select bind:value="{$apiBase}">
                {#each config.apiBase as a}
                    <option value="{a.url}">{a.name}</option>
                {/each}
            </select>
        </label>
    </div>
    <div>
        {#if $apiToken}
            {#if !files?.length}
                <label
                    >Excel file for import:
                    <input
                        type="file"
                        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        bind:files="{files}"
                    />
                </label>
            {:else}
                <b>{files[0].name}</b>
                <button
                    on:click="{() => {
                        files = []
                        clearState()
                    }}">verwerfen</button
                >
            {/if}
        {/if}
    </div>
    <div style="flex-grow: 1; padding-right: 10px; text-align: right;">
        {#if stats.all}<span class="chip">{stats.all} items</span>{/if}
        <!-- svelte-ignore a11y-missing-attribute -->
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <a class="qLink" on:click="{() => (showSyncQueue = true)}">
            {#if stats.new}<span class="chip green">{stats.new} new</span>{/if}
            {#if stats.change}<span class="chip yellow">{stats.change} changed</span>{/if}
            {#if stats.delete}<span class="chip red">{stats.delete} deleted</span>{/if}
        </a>
        <button class="sync" disabled="{toSync.length == 0}" on:click="{sync}">synchronize</button>
    </div>
</div>

<div style="display:flex">
    <div>
        <NavTree items="{nav?.subNav}" />
    </div>
    <div style="flex-grow: 1; padding-left: 20px;">
        <ProductTable />
    </div>
</div>

{#if showSyncQueue}
    <SyncQueueOverlay
        items="{toSync}"
        close="{() => {
            showSyncQueue = false
        }}"
    />
{/if}

<JsonOverlay bind:json="{$jsonForOverlay}" />
<Loader text="{loaderText}" />

<style>
    :global(body),
    :global(document) {
        padding: 0;
        margin: 0;
    }
    :global(.chip) {
        display: inline-block;
        padding: 1px;
        padding-left: 3px;
        padding-right: 3px;
        border-radius: 5px;
        color: white;
        background-color: black;
        margin-left: 5px;
    }

    :global(.chip.green) {
        background-color: lightgreen;
        color: black;
    }
    :global(.chip.red) {
        background-color: red;
        color: black;
    }
    :global(.chip.yellow) {
        background-color: yellow;
        color: black;
    }

    .qLink {
        cursor: pointer;
    }

    .header {
        background-color: #555;
        color: white;
        padding: 5px;
    }
    button.sync {
        font-weight: bold;
        color: darkgreen;
        vertical-align: bottom;
    }
</style>
